import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, BookOpen, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { t } from "@/lib/i18n";
import { Footer } from "@/components/Footer";

export default function CelebrationPage() {
  useNoIndex();
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/juhla/${token}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen grid place-items-center text-[#7A6B63]">{t("common.loading")}</div>;
  if (notFound || !doc)
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center">
        <div><h1 className="font-serif text-3xl text-[#2C221E]">{t("common.notFound")}</h1></div>
      </div>
    );

  if (!doc.portrait_ready)
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center" data-testid="celebration-not-ready">
        <div>
          <h1 className="font-serif text-3xl text-[#2C221E]">{t("celebration.notReady.title")}</h1>
          <p className="mt-2 text-[#7A6B63]">{t("celebration.notReady.body")}</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <div className="flex items-center gap-2 text-[#C86D51] mb-4">
          <Gift className="w-6 h-6" />
          <span className="text-sm font-semibold tracking-wide uppercase">Portwrite</span>
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#2C221E]">{doc.person_name}</h1>
          <p className="mt-2 text-[#7A6B63]">{doc.occasion_title}</p>
          <div
            className="mt-8 rounded-3xl bg-white border border-[#E8DEC2] p-6 sm:p-8 shadow-sm font-serif text-[17px] leading-loose text-[#2C221E] whitespace-pre-wrap"
            data-testid="celebration-portrait"
          >
            {doc.portrait_text}
          </div>

          {doc.book_ready && (
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`${API}/juhla/${token}/web`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl bg-[#C86D51] hover:bg-[#B35A3E] text-white font-medium"
                data-testid="celebration-read-all"
              >
                <BookOpen className="w-5 h-5 mr-2" /> {t("celebration.readAll")}
              </a>
              <a
                href={`${API}/juhla/${token}/book.pdf`}
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] font-medium"
                data-testid="celebration-download-book"
              >
                <Download className="w-5 h-5 mr-2" /> {t("celebration.downloadBook")}
              </a>
            </div>
          )}
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
